import React, { useState, useEffect, useRef, ErrorInfo, Component } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import { BlogPost } from '../types/blogPost';
import { getBlogPosts } from '../services/api';
import { FaRocket, FaClock, FaEye, FaArrowRight } from 'react-icons/fa';

// Error boundary specifically for the slider
class SliderErrorBoundary extends Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('CosmicContentSlider Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      // Render fallback UI that doesn't crash
      return (
        <SliderFallback>
          <FallbackContent>
            <FallbackIcon>🌌</FallbackIcon>
            <FallbackText>Cosmic content temporarily unavailable</FallbackText>
            <FallbackSubtext>Our space engineers are fixing this</FallbackSubtext>
          </FallbackContent>
        </SliderFallback>
      );
    }

    return this.props.children;
  }
}

interface CosmicContentSliderProps {
  autoSlideInterval?: number;
  showCount?: number;
}

const CosmicContentSlider: React.FC<CosmicContentSliderProps> = ({ 
  autoSlideInterval = 4000,
  showCount = 5 
}) => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(true);
  const sliderRef = useRef<HTMLDivElement>(null);

  // Fetch random blog posts and newsletters with comprehensive error handling
  useEffect(() => {
    const fetchContent = async () => {
      try {
        setLoading(true);
        console.log('CosmicContentSlider: Attempting to fetch blog posts...');
        
        // Add timeout to prevent hanging requests
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
        
        const response = await getBlogPosts(1, 20); // Get more posts to randomize
        clearTimeout(timeoutId);
        
        const allPosts = response.posts || [];
        
        if (allPosts.length > 0) {
          console.log(`CosmicContentSlider: Successfully fetched ${allPosts.length} posts`);
          // Shuffle and select random posts
          const shuffled = allPosts.sort(() => 0.5 - Math.random());
          const selected = shuffled.slice(0, showCount);
          setPosts(selected);
        } else {
          console.log('CosmicContentSlider: No posts returned from API, using fallback');
          await loadFallbackContent();
        }
      } catch (error: any) {
        console.error('CosmicContentSlider: Error fetching content, using fallback:', error);
        // Log more details about the error
        if (error.response) {
          console.error('Response status:', error.response.status);
          console.error('Response data:', error.response.data);
        }
        await loadFallbackContent();
      } finally {
        setLoading(false);
      }
    };

    const loadFallbackContent = async () => {
      try {
        // Always provide fallback content instead of empty array
        const BlogCache = (await import('../services/blogCache')).default;
        const fallbackPosts = BlogCache.getFallbackPosts();
        const selected = fallbackPosts.slice(0, showCount);
        setPosts(selected);
        console.log(`CosmicContentSlider: Using ${selected.length} fallback posts`);
      } catch (fallbackError) {
        console.error('CosmicContentSlider: Even fallback failed, using emergency content:', fallbackError);
        // Ultimate fallback - create sample content that will never fail
        setPosts([{
          id: 999999,
          title: '🚀 Exploring the Cosmic Frontier',
          content: 'Discover the latest breakthroughs in space exploration and satellite technology. Our team brings you the most exciting developments from the cosmos.',
          excerpt: 'Discover the latest breakthroughs in space exploration and satellite technology...',
          author: 'SpacePings Team',
          published_date: new Date().toISOString(),
          slug: 'cosmic-frontier',
          content_type: 'blog' as const,
          reading_time: 5,
          view_count: 0,
          keywords: ['space', 'exploration', 'technology'],
          meta_description: 'Latest space exploration breakthroughs and satellite technology updates from SpacePings.',
          word_count: 150
        }]);
      }
    };

    // Wrap in try-catch to prevent any uncaught errors
    try {
      fetchContent();
    } catch (error) {
      console.error('CosmicContentSlider: Critical error in useEffect:', error);
      setLoading(false);
      setPosts([]); // Emergency fallback
    }
  }, [showCount]);

  // Auto-slide functionality
  useEffect(() => {
    if (!isHovered && posts.length > 1) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => 
          prevIndex === posts.length - 1 ? 0 : prevIndex + 1
        );
      }, autoSlideInterval);

      return () => clearInterval(interval);
    }
  }, [isHovered, posts.length, autoSlideInterval]);

  // Smooth scroll to current card
  useEffect(() => {
    if (sliderRef.current && posts.length > 0) {
      const cardWidth = 320; // Width of each card + gap
      const scrollPosition = currentIndex * cardWidth;
      
      sliderRef.current.scrollTo({
        left: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [currentIndex, posts.length]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getContentUrl = (post: BlogPost) => {
    return post.content_type === 'newsletter' 
      ? `/newsletter/${post.slug}` 
      : `/blog/${post.slug}`;
  };

  const getContentType = (post: BlogPost) => {
    return post.content_type === 'newsletter' ? 'Newsletter' : 'Article';
  };

  if (loading) {
    return (
      <SliderContainer>
        <SectionHeader>
          <Title>🌌 Mission Briefings</Title>
          <Subtitle>Cosmic content loading...</Subtitle>
        </SectionHeader>
        <LoadingContainer>
          <LoadingSpinner />
          <LoadingText>Establishing uplink to galactic archive...</LoadingText>
        </LoadingContainer>
      </SliderContainer>
    );
  }

  // Always show the slider - even with fallback content
  if (posts.length === 0 && !loading) {
    console.error('CosmicContentSlider: No posts available and not loading - this should not happen');
    // This should never happen due to our robust fallback system
  }

  return (
    <SliderContainer>
      <SectionHeader>
        <Title>🌌 Mission Briefings</Title>
        <Subtitle>Explore cosmic discoveries and space intel</Subtitle>
      </SectionHeader>

      <SliderWrapper
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <CosmicBackground>
          <Star style={{ '--delay': '0s', '--x': '10%', '--y': '20%' } as React.CSSProperties}>✨</Star>
          <Star style={{ '--delay': '1s', '--x': '80%', '--y': '10%' } as React.CSSProperties}>⭐</Star>
          <Star style={{ '--delay': '2s', '--x': '60%', '--y': '80%' } as React.CSSProperties}>🌟</Star>
          <Star style={{ '--delay': '3s', '--x': '30%', '--y': '70%' } as React.CSSProperties}>💫</Star>
          <Star style={{ '--delay': '4s', '--x': '90%', '--y': '60%' } as React.CSSProperties}>⭐</Star>
        </CosmicBackground>

        <ContentTrack ref={sliderRef}>
          {posts.map((post, index) => (
            <ContentCard 
              key={post.id}
              as={Link}
              to={getContentUrl(post)}
              $isActive={index === currentIndex}
            >
              <CardHeader>
                <ContentBadge $type={post.content_type}>
                  <FaRocket />
                  {getContentType(post)}
                </ContentBadge>
                <ContentMeta>
                  <MetaItem>
                    <FaClock />
                    {post.reading_time || 3} min
                  </MetaItem>
                  {post.view_count && (
                    <MetaItem>
                      <FaEye />
                      {post.view_count}
                    </MetaItem>
                  )}
                </ContentMeta>
              </CardHeader>

              <CardContent>
                <CardTitle>{post.title}</CardTitle>
                <CardExcerpt>{post.excerpt}</CardExcerpt>
                
                <CardFooter>
                  <Author>By {post.author}</Author>
                  <PublishDate>{formatDate(post.published_date)}</PublishDate>
                </CardFooter>
              </CardContent>

              <CardAction>
                <ActionText>Read More</ActionText>
                <FaArrowRight />
              </CardAction>

              <CosmicGlow />
            </ContentCard>
          ))}
        </ContentTrack>

        <SliderIndicators>
          {posts.map((_, index) => (
            <Indicator
              key={index}
              $isActive={index === currentIndex}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </SliderIndicators>
      </SliderWrapper>

      <ViewAllButton as={Link} to="/blog">
        <span>Explore Full Archive</span>
        <FaRocket />
      </ViewAllButton>
    </SliderContainer>
  );
};

// Animations
const twinkle = keyframes`
  0%, 100% { opacity: 0.3; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.2); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(139, 92, 246, 0.3); }
  50% { box-shadow: 0 0 40px rgba(139, 92, 246, 0.6); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const slideIn = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Styled Components
const SliderContainer = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  position: relative;
  overflow: hidden;
  animation: ${slideIn} 1s ease-out;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(139, 92, 246, 0.05) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  position: relative;
  z-index: 2;
`;

const Title = styled.h2`
  font-family: 'Orbitron', sans-serif;
  font-size: 2.5rem;
  font-weight: 700;
  color: #c084fc;
  margin-bottom: 0.5rem;
  text-shadow: 0 0 20px rgba(192, 132, 252, 0.5);

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.125rem;
  color: #94a3b8;
  margin: 0;
`;

const SliderWrapper = styled.div`
  position: relative;
  max-width: 1400px;
  margin: 0 auto;
  padding: 2rem 0;
`;

const CosmicBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
`;

const Star = styled.span`
  position: absolute;
  font-size: 16px;
  animation: ${twinkle} 3s ease-in-out infinite;
  animation-delay: var(--delay);
  left: var(--x);
  top: var(--y);
`;

const ContentTrack = styled.div`
  display: flex;
  gap: 2rem;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 1rem 0;
  position: relative;
  z-index: 2;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    gap: 1rem;
  }
`;

const ContentCard = styled.div<{ $isActive: boolean }>`
  min-width: 300px;
  max-width: 300px;
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 16px;
  padding: 1.5rem;
  position: relative;
  text-decoration: none;
  color: inherit;
  transition: all 0.4s ease;
  animation: ${float} 6s ease-in-out infinite;
  animation-delay: ${props => props.$isActive ? '0s' : '1s'};
  backdrop-filter: blur(10px);

  &:hover {
    transform: translateY(-10px) scale(1.02);
    border-color: rgba(139, 92, 246, 0.6);
    box-shadow: 
      0 20px 40px rgba(0, 0, 0, 0.3),
      0 0 30px rgba(139, 92, 246, 0.4);
  }

  ${props => props.$isActive && css`
    border-color: rgba(139, 92, 246, 0.6);
    animation: ${glow} 2s ease-in-out infinite;
  `}

  @media (max-width: 768px) {
    min-width: 280px;
    max-width: 280px;
    padding: 1.25rem;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const ContentBadge = styled.div<{ $type?: string }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.$type === 'newsletter' 
    ? 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)'
    : 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)'
  };
  color: white;
`;

const ContentMeta = styled.div`
  display: flex;
  gap: 0.75rem;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #94a3b8;

  svg {
    font-size: 0.625rem;
  }
`;

const CardContent = styled.div`
  margin-bottom: 1rem;
`;

const CardTitle = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #e0e7ff;
  margin-bottom: 0.75rem;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardExcerpt = styled.p`
  font-size: 0.875rem;
  color: #94a3b8;
  line-height: 1.5;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid rgba(139, 92, 246, 0.2);
`;

const Author = styled.span`
  font-size: 0.75rem;
  color: #8b5cf6;
  font-weight: 500;
`;

const PublishDate = styled.span`
  font-size: 0.75rem;
  color: #64748b;
`;

const CardAction = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #8b5cf6;
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 1rem;
  transition: all 0.3s ease;

  ${ContentCard}:hover & {
    color: #a78bfa;
    transform: translateX(5px);
  }
`;

const ActionText = styled.span`
  flex: 1;
`;

const CosmicGlow = styled.div`
  position: absolute;
  top: -2px;
  left: -2px;
  right: -2px;
  bottom: -2px;
  background: linear-gradient(45deg, transparent, rgba(139, 92, 246, 0.3), transparent);
  border-radius: 16px;
  opacity: 0;
  z-index: -1;
  transition: opacity 0.3s ease;

  ${ContentCard}:hover & {
    opacity: 1;
  }
`;

const SliderIndicators = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  margin-top: 2rem;
`;

const Indicator = styled.button<{ $isActive: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.$isActive 
    ? 'linear-gradient(135deg, #8b5cf6 0%, #c084fc 100%)'
    : 'rgba(139, 92, 246, 0.3)'
  };
  
  &:hover {
    background: rgba(139, 92, 246, 0.6);
    transform: scale(1.2);
  }

  ${props => props.$isActive && css`
    box-shadow: 0 0 10px rgba(139, 92, 246, 0.6);
  `}
`;

const ViewAllButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin: 3rem auto 0;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  border: none;
  border-radius: 50px;
  color: white;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  max-width: 300px;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(139, 92, 246, 0.4);
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.5s ease;
  }

  &:hover::before {
    left: 100%;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 3px solid rgba(139, 92, 246, 0.3);
  border-top: 3px solid #8b5cf6;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 1rem;
`;

const LoadingText = styled.p`
  color: #94a3b8;
  font-size: 1rem;
  text-align: center;
`;

// Fallback styled components
const SliderFallback = styled.div`
  padding: 4rem 2rem;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  border-radius: 16px;
  margin: 2rem 0;
`;

const FallbackContent = styled.div`
  text-align: center;
  max-width: 400px;
  margin: 0 auto;
`;

const FallbackIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
`;

const FallbackText = styled.h3`
  color: #e0e7ff;
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
`;

const FallbackSubtext = styled.p`
  color: #94a3b8;
  font-size: 1rem;
  margin: 0;
`;

// Wrapped component with error boundary
const SafeCosmicContentSlider: React.FC<CosmicContentSliderProps> = (props) => (
  <SliderErrorBoundary>
    <CosmicContentSlider {...props} />
  </SliderErrorBoundary>
);

export default SafeCosmicContentSlider;